/**
 * Prototype: webapp Visual frontmatter bar vs TOC CurrentSectionPill.
 * Access: /?test=viewer-frontmatter-bar
 *
 * Two stacked breakpoints (wide then narrow) matching live viewer geometry:
 *   1. Margin - wide panel: pill fixed in left gutter; frontmatter full reading width
 *   2. Content - narrow panel: pill fixed over reading left; frontmatter compact, right-aligned
 *
 * Pill is a sibling of the scroll area (not inside it) so it stays pinned while body scrolls.
 *
 * Bet: docs/product/bets/2026-07-16-webapp-viewer-frontmatter-bar.md
 */

import { useMemo, useState } from 'react'
import {
  ChevronRight,
  Clock,
  Code2,
  FileText,
  Info,
  List,
  MoreVertical,
  Save,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const MOCK_FIELDS: Array<{ key: string; value: string }> = [
  { key: 'status', value: 'ready' },
  { key: 'appetite', value: 'small' },
  { key: 'owner', value: 'Rowan' },
  { key: 'tags', value: 'ui, editor' },
  { key: 'dispatch_target', value: 'cursor-cloud' },
  { key: 'design_required', value: 'prototype' },
  { key: 'safe_to_dispatch', value: 'false' },
  { key: 'date', value: '2026-07-16' },
]

type Scene = 'collapsed' | 'expanded' | 'with-banner'
type Breakpoint = 'margin' | 'content'

const SCENES: { id: Scene; label: string }[] = [
  { id: 'collapsed', label: 'Collapsed' },
  { id: 'expanded', label: 'Expanded' },
  { id: 'with-banner', label: 'Change banner' },
]

const DOC_TITLE = 'Add Clarity tags / scripts for SEO and NEW (Apollo) script for Apollo CRM'
const DOC_TITLE_SHORT = 'Add Clarity tags and Apollo CRM script'

/** Wide reading column starts after this gutter; pill must stay inside it. */
const MARGIN_GUTTER_PX = 240
const MARGIN_PILL_LEFT_PX = 24
/** Leave a gap before the reading column edge so the pill never overlaps the bar. */
const MARGIN_PILL_MAX_PX = MARGIN_GUTTER_PX - MARGIN_PILL_LEFT_PX - 16 // 200

function TocPill({
  label,
  topPx,
  /** When true, cap width so the pill cannot spill past the left gutter. */
  inGutter,
}: {
  label: string
  topPx: number
  inGutter?: boolean
}) {
  return (
    <div
      className="pointer-events-auto absolute z-30"
      style={{
        top: topPx,
        left: MARGIN_PILL_LEFT_PX,
        width: inGutter
          ? MARGIN_PILL_MAX_PX
          : 'min(220px, calc(100% - 3rem))',
        maxWidth: inGutter ? MARGIN_PILL_MAX_PX : undefined,
      }}
      data-testid="prototype-toc-pill"
    >
      <button
        type="button"
        title="Open table of contents"
        className={cn(
          'inline-flex items-center gap-1.5 w-full',
          'px-2.5 py-1.5 rounded-full border text-xs',
          'bg-background/95 border-border text-muted-foreground shadow-sm text-left',
          'hover:bg-accent hover:text-foreground hover:border-primary/30',
        )}
      >
        <ChevronRight className="w-3 h-3 shrink-0 opacity-60" />
        <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium">
          {label}
        </span>
      </button>
    </div>
  )
}

const MOCK_YAML = [
  '---',
  ...MOCK_FIELDS.map((row) => `${row.key}: ${row.value}`),
  '---',
].join('\n')

/** Expanded frontmatter as a document-relative YAML code block (above the title). */
function FrontmatterCodeBlock() {
  return (
    <pre
      className={cn(
        'w-full max-h-52 overflow-auto rounded-md border border-border/50',
        'bg-muted/40 px-3 py-2.5 text-left',
        'font-mono text-[11px] leading-relaxed text-foreground/90',
      )}
      data-testid="prototype-frontmatter-code"
    >
      <code>{MOCK_YAML}</code>
    </pre>
  )
}

/**
 * Toggle label: YAML fence prefix (---) beats <> chevrons; no box / Expand.
 * Expanded YAML renders in document flow via FrontmatterCodeBlock.
 */
function FrontmatterBar({
  expanded,
  onToggle,
  layout,
}: {
  expanded: boolean
  onToggle: () => void
  layout: Breakpoint
}) {
  // Narrow: right (clear of fixed pill). Wide: left of reading column (pill is in gutter).
  const alignEnd = layout === 'content'

  return (
    <div
      className={cn('flex', alignEnd ? 'justify-end' : 'justify-start')}
      data-testid="prototype-frontmatter-bar"
      data-layout={layout}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={cn(
          'font-mono text-xs text-muted-foreground hover:text-foreground',
          'underline-offset-2 hover:underline',
        )}
      >
        --- frontmatter
      </button>
    </div>
  )
}

function ViewerChrome({
  title,
  showTitle,
}: {
  title: string
  showTitle: boolean
}) {
  return (
    <div className="shrink-0 border-b border-border bg-card">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <div className="min-w-0 flex-1 text-xs text-muted-foreground truncate">
          ... &gt; exida &gt; todos
        </div>
        <div className="flex items-center gap-0.5 text-muted-foreground">
          <ToolbarIcon icon={Save} label="Save" />
          <ToolbarIcon icon={List} label="TOC" />
          <ToolbarIcon icon={Clock} label="History" />
          <ToolbarIcon icon={Info} label="Details" />
          <ToolbarIcon icon={Code2} label="Mode" />
          <ToolbarIcon icon={MoreVertical} label="More" />
        </div>
      </div>
      {showTitle ? (
        <div className="px-4 pb-2.5">
          <h1 className="text-base font-semibold tracking-tight text-foreground leading-snug">
            {title}
          </h1>
        </div>
      ) : null}
    </div>
  )
}

function ToolbarIcon({
  icon: Icon,
  label,
}: {
  icon: typeof Save
  label: string
}) {
  return (
    <button
      type="button"
      title={label}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent hover:text-foreground"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  )
}

function ReadingBody({
  showInlineTitle,
  /** YAML block in document flow, immediately above the title (narrow expand). */
  frontmatterCode,
}: {
  showInlineTitle: boolean
  frontmatterCode?: boolean
}) {
  return (
    <div className="space-y-4">
      {frontmatterCode ? <FrontmatterCodeBlock /> : null}
      {showInlineTitle ? (
        <h1 className="text-2xl font-semibold tracking-tight text-foreground leading-snug">
          {DOC_TITLE}
        </h1>
      ) : null}

      <p className="text-sm leading-relaxed">
        <span className="text-muted-foreground">Basecamp: </span>
        <a className="text-blue-500 hover:underline" href="#todo">
          View Todo
        </a>
        <span className="text-muted-foreground"> - </span>
        <span className="text-muted-foreground">GitLab MR: </span>
        <a className="text-blue-500 hover:underline" href="#mr">
          !13 Marketing tags + template sync
        </a>
        <span className="text-muted-foreground"> - merged</span>
      </p>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold tracking-tight">Requirements</h2>
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-foreground/90">
          <li>Microsoft Clarity tag on landing layout pages</li>
          <li>Apollo CRM tracker on landing layout pages</li>
          <li>
            Shared partial{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">PAR_MARKETING_TAGS</code>
          </li>
          <li>Scope limited to page-builder landing layout</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-semibold tracking-tight">Implementation</h2>
        <p className="text-sm text-foreground/90 leading-relaxed">
          Scroll this column - the TOC pill stays pinned to the viewport. Frontmatter is in-flow
          and scrolls away with the body.
        </p>
        {Array.from({ length: 6 }).map((_, i) => (
          <p key={i} className="text-sm text-muted-foreground leading-relaxed">
            Filler paragraph {i + 1}. Pill stays fixed; Expand must remain clear of the pill hit
            box at the top of the scroll.
          </p>
        ))}
      </section>
    </div>
  )
}

function BreakpointFrame({
  breakpoint,
  expanded,
  showBanner,
  onToggle,
}: {
  breakpoint: Breakpoint
  expanded: boolean
  showBanner: boolean
  onToggle: () => void
}) {
  const isMargin = breakpoint === 'margin'
  const bannerHeight = showBanner ? 40 : 0
  const pillTop = 8 + bannerHeight

  return (
    <section className="space-y-2" data-testid={`prototype-frame-${breakpoint}`}>
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-foreground">
          {isMargin ? '1. Margin breakpoint (wide)' : '2. Content breakpoint (narrow)'}
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          {isMargin
            ? 'Pill fixed in the left gutter. --- frontmatter link at top of reading column; expand = YAML block above body.'
            : 'Pill fixed over reading left. --- frontmatter link right; expand = YAML code block above the title.'}
        </p>
      </div>

      {/* Outer width encodes the breakpoint - stacked so wide can actually be wide */}
      <div
        className={cn(
          'rounded-lg border border-border bg-card overflow-hidden shadow-sm',
          isMargin ? 'w-full' : 'w-full max-w-[440px]',
        )}
      >
        <ViewerChrome
          title={isMargin ? DOC_TITLE_SHORT : DOC_TITLE}
          showTitle={isMargin}
        />

        {showBanner ? (
          <div className="bg-amber-500/10 border-b border-amber-500/25 px-3 py-2 text-[11px] text-amber-950 dark:text-amber-100">
            Unsaved Visual changes - pill top offset +{bannerHeight}px
          </div>
        ) : null}

        {/* Frame: pill is a sibling of the scroller so it does not scroll away */}
        <div className="relative h-[400px]" data-testid="prototype-viewer-stage">
          <TocPill
            label={isMargin ? 'Requirements' : 'Add Clarity tags / scripts for ...'}
            topPx={pillTop}
            inGutter={isMargin}
          />

          <div className="h-full overflow-y-auto">
            <div
              className={cn(
                'px-6 py-4 space-y-3',
                isMargin
                  ? // Reading column starts after gutter; pill max width stays inside that band
                    'mr-auto max-w-[760px] pl-0'
                  : 'max-w-none',
              )}
              style={isMargin ? { marginLeft: MARGIN_GUTTER_PX } : undefined}
            >
              <FrontmatterBar
                expanded={expanded}
                onToggle={onToggle}
                layout={breakpoint}
              />
              <ReadingBody
                showInlineTitle={!isMargin}
                frontmatterCode={expanded}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function TestViewerFrontmatterBar() {
  const [scene, setScene] = useState<Scene>('collapsed')
  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null)

  const expanded = useMemo(() => {
    if (expandedOverride != null) return expandedOverride
    return scene === 'expanded'
  }, [expandedOverride, scene])

  const showBanner = scene === 'with-banner'
  const toggleExpanded = () => setExpandedOverride(!expanded)

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-[1100px] px-6 py-8 space-y-8">
        <header className="space-y-3 max-w-2xl">
          <div className="flex items-center gap-2 text-muted-foreground">
            <FileText className="h-5 w-5" />
            <span className="text-xs font-medium uppercase tracking-wide">Prototype</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Viewer frontmatter bar</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            TOC pill is pinned to the scroll viewport. Both breakpoints: --- frontmatter text
            link (YAML fence cue); expand opens a code block in document flow. Bet:
            2026-07-16-webapp-viewer-frontmatter-bar.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          {SCENES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={scene === s.id ? 'default' : 'outline'}
              onClick={() => {
                setScene(s.id)
                setExpandedOverride(null)
              }}
            >
              {s.label}
            </Button>
          ))}
        </div>

        {/* Stacked: wide first (full stage width), then narrow */}
        <div className="space-y-10">
          <BreakpointFrame
            breakpoint="margin"
            expanded={expanded}
            showBanner={showBanner}
            onToggle={toggleExpanded}
          />
          <BreakpointFrame
            breakpoint="content"
            expanded={expanded}
            showBanner={showBanner}
            onToggle={toggleExpanded}
          />
        </div>

        <ul className="text-xs text-muted-foreground space-y-1.5 max-w-2xl border-t border-border pt-6">
          <li>
            Pill is outside the scroller (absolute on the frame) - scroll the body; pill stays put.
          </li>
          <li>
            Both: --- frontmatter mono link (no box). Expand = YAML {'<pre>'} in document flow.
          </li>
          <li>
            Narrow link right-aligned (clear of pill); wide link left in the reading column.
          </li>
          <li>
            Wide: 240px gutter; pill capped at 200px so it cannot spill into the reading column.
          </li>
        </ul>
      </div>
    </div>
  )
}
